import type { ExternalLayer } from 'hooks/externalLayer/types';
import { formatDateTime } from 'components/IAP/Resources/utils/dateUtils';
import { LayerFeaturesContent } from './LayerFeaturesContent';
import { LayerMetadata } from './LayerDataRows';
import { Layer } from './Layer';
import { useUscgMapContext } from './UscgMapContext';
import { resolveLayerProvenance } from './utils/layerProvenance';
import { FeedLayerControls } from './FeedLayerControls';
import { isFeedUri } from 'components/MapPage/CorporateViewTabs/EventsTab/externalFeeds';

export function ExternalLayerItem({
  layer,
  onDelete,
  onOpenInNewClick,
  onZoomToLayer,
  onZoomToGraphic,
  dataTour,
  zoomTour,
  qualityTour,
}: {
  layer: ExternalLayer;
  onDelete?: () => void;
  onOpenInNewClick?: () => void;
  onZoomToLayer?: () => void;
  onZoomToGraphic: (graphic: __esri.Graphic) => void;
  /** `data-tour` on the layer row for product tours. */
  dataTour?: string;
  /** `data-tour` on the zoom control for product tours. */
  zoomTour?: string;
  /** `data-tour` on the provenance badge for product tours. */
  qualityTour?: string;
}) {
  const eventKey = `external-${layer.id}`;
  const { isExternalLayerLoadError } = useUscgMapContext();
  const hasLoadError = isExternalLayerLoadError(layer.id);
  const provenance = resolveLayerProvenance(layer.serviceUrl);
  const isFeed = isFeedUri(layer.serviceUrl);

  return (
    <Layer.Item
      eventKey={eventKey}
      dataTour={dataTour}
      cardClassName={
        hasLoadError ? 'bg-danger-subtle border-danger' : undefined
      }
      header={
        <Layer.Header
          title={layer.name}
          layerId={layer.id}
          layerType="external"
          provenanceBadge={provenance.badge}
          error={
            hasLoadError
              ? isFeed
                ? `Could not load the ${provenance.source} forecast. Try Refresh.`
                : `Failed to load layer from ${provenance.source}. Please check your permissions. (itemId: ${layer.arcgisItemId})`
              : undefined
          }
          onOpenInNew={onOpenInNewClick}
          onZoomToLayer={onZoomToLayer}
          zoomTour={zoomTour}
          qualityTour={qualityTour}
          onDelete={onDelete}
          pickPayload={{
            name: layer.name || 'Untitled external layer',
            serviceUrl: layer.serviceUrl,
            arcgisItemId: layer.arcgisItemId,
          }}
        />
      }
    >
      <Layer.Content>
        {isFeed ? <FeedLayerControls layer={layer} /> : null}
        <LayerMetadata
          lastUpdated={formatDateTime(layer.updatedAt)}
          aorIds={layer.aorIds}
          incident={layer.incidentId}
          sources={provenance.source}
        />
        <LayerFeaturesContent
          layer={layer}
          layerType="external"
          onZoomToExternalGraphic={onZoomToGraphic}
        />
      </Layer.Content>
    </Layer.Item>
  );
}
